import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import apiService from "../services/api";
import MentorAssignmentManager from "../components/mentor-assignments/MentorAssignmentManager";

const cardStyles = {
  card: { background: "white", borderRadius: "0.5rem", padding: "1.5rem", border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", transition: "transform 0.2s, box-shadow 0.2s" },
  cardName: { fontSize: "1.125rem", fontWeight: "600", color: "#001272", margin: "0 0 1rem" },
  row: { display: "flex", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: "1px solid #f1f5f9", fontSize: "0.875rem" },
  rowLabel: { color: "#001272", opacity: 0.7 },
  rowVal: { fontWeight: "500", color: "#001272" },
  mentorBadge: { fontWeight: "500", color: "#166534" },
  noMentor: { fontWeight: "500", color: "#9ca3af", fontStyle: "italic" },
  btn: { width: "100%", marginTop: "1rem", padding: "0.5rem 1rem", backgroundColor: "#001272", color: "white", border: "none", borderRadius: "0.375rem", fontSize: "0.875rem", fontWeight: "500", cursor: "pointer" },
};

const TutorCard = ({ tutor, onManage }) => {
  const fullName = `${tutor.firstName || ""} ${tutor.lastName || ""}`.trim() || "Unnamed Tutor";

  const mentorName = tutor.currentMentor
    ? `${tutor.currentMentor.mentorFirstName || ""} ${tutor.currentMentor.mentorLastName || ""}`.trim()
    : null;

  return (
    <div
      style={cardStyles.card}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0,0,0,0.1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = cardStyles.card.boxShadow; }}
    >
      <h3 style={cardStyles.cardName}>{fullName}</h3>
      <div style={cardStyles.row}>
        <span style={cardStyles.rowLabel}>Email</span>
        <span style={cardStyles.rowVal}>{tutor.email || "—"}</span>
      </div>
      {tutor.primaryTimezone && (
        <div style={cardStyles.row}>
          <span style={cardStyles.rowLabel}>Timezone</span>
          <span style={cardStyles.rowVal}>{tutor.primaryTimezone}</span>
        </div>
      )}
      <div style={cardStyles.row}>
        <span style={cardStyles.rowLabel}>Current Mentor</span>
        {mentorName
          ? <span style={cardStyles.mentorBadge}>{mentorName}</span>
          : <span style={cardStyles.noMentor}>None assigned</span>
        }
      </div>
      <button
        style={cardStyles.btn}
        onMouseEnter={(e) => { e.target.style.backgroundColor = "#000d5c"; }}
        onMouseLeave={(e) => { e.target.style.backgroundColor = "#001272"; }}
        onClick={() => onManage(tutor.canonicalTutorId, fullName)}
      >
        Assign Mentor
      </button>
    </div>
  );
};

const MentorsPage = () => {
  const [selectedTutorId, setSelectedTutorId] = useState(null);
  const [selectedTutorName, setSelectedTutorName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("tutors");
  const [showDormant, setShowDormant] = useState(false);

  const { data: tutorsData, isLoading, error } = useQuery({
    queryKey: ["tutors", 0, 500],
    queryFn: () => apiService.getTutors(0, 500),
    staleTime: 5 * 60 * 1000,
  });

  const allTutors = (() => {
    if (!tutorsData) return [];
    if (Array.isArray(tutorsData)) return tutorsData;
    if (tutorsData.content) return tutorsData.content;
    return [];
  })();

  const tutors = allTutors.filter((t) => showDormant || !t.isDormant);

  const filtered = tutors.filter((t) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return `${t.firstName || ""} ${t.lastName || ""} ${t.email || ""}`.toLowerCase().includes(s);
  });


  const openModal = (tutorId, tutorName) => {
    setSelectedTutorId(tutorId);
    setSelectedTutorName(tutorName);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTutorId(null);
    setSelectedTutorName("");
  };

  // Group tutors by mentor for the "By Mentor" tab (uses filtered list)
  const byMentor = tutors.reduce((acc, t) => {
    const name = t.currentMentor
      ? `${t.currentMentor.mentorFirstName || ""} ${t.currentMentor.mentorLastName || ""}`.trim()
      : null;
    const key = name || "__unassigned__";
    if (!acc[key]) acc[key] = { mentorName: name, tutors: [] };
    acc[key].tutors.push(t);
    return acc;
  }, {});

  const mentorGroups = Object.values(byMentor).sort((a, b) => {
    if (!a.mentorName) return 1;
    if (!b.mentorName) return -1;
    return a.mentorName.localeCompare(b.mentorName);
  });

  const s = {
    page: { padding: "1rem 0" },
    header: { marginBottom: "1.5rem" },
    h1: { fontSize: "1.875rem", fontWeight: "700", color: "#001272", marginBottom: "0.5rem" },
    subtitle: { color: "#001272", opacity: "0.8", fontSize: "1rem" },
    tabs: { display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: "1.5rem" },
    tab: { padding: "0.6rem 1.25rem", fontSize: "0.875rem", fontWeight: "500", cursor: "pointer", border: "none", background: "none", color: "#64748b", borderBottom: "2px solid transparent", marginBottom: "-2px" },
    tabActive: { color: "#001272", borderBottomColor: "#001272", fontWeight: "600" },
    controls: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" },
    search: { padding: "0.5rem 1rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.875rem", minWidth: "300px", outline: "none" },
    count: { color: "#6b7280", fontSize: "0.875rem" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1.5rem" },
    emptyState: { textAlign: "center", padding: "3rem 1rem", color: "#6b7280" },
    loadingWrap: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "4rem 0" },
    spinner: { border: "3px solid #f3f3f3", borderTop: "3px solid #53bdeb", borderRadius: "50%", width: "40px", height: "40px", animation: "spin 1s linear infinite", marginBottom: "1rem" },
    errBox: { background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "0.375rem", padding: "1rem", color: "#991b1b" },
    groupCard: { background: "white", borderRadius: "0.5rem", border: "1px solid #e2e8f0", marginBottom: "1rem", overflow: "hidden" },
    groupHeader: { padding: "0.75rem 1rem", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: "0.75rem" },
    groupName: { fontWeight: "600", color: "#001272", fontSize: "0.95rem" },
    groupCount: { fontSize: "0.75rem", color: "#6b7280", background: "#e2e8f0", borderRadius: "9999px", padding: "0.1rem 0.5rem" },
    tutorRow: { padding: "0.6rem 1rem", borderBottom: "1px solid #f1f5f9", fontSize: "0.875rem", display: "flex", justifyContent: "space-between", alignItems: "center", color: "#374151" },
  };

  if (isLoading) return (
    <div style={s.page}>
      <div style={s.header}><h1 style={s.h1}>Mentor Management</h1></div>
      <div style={s.loadingWrap}>
        <div style={s.spinner} />
        <p style={{ color: "#001272" }}>Loading tutors...</p>
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={s.page}>
      <div style={s.header}><h1 style={s.h1}>Mentor Management</h1></div>
      <div style={s.errBox}>Error loading tutors: {error.message}</div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.h1}>Mentor Management</h1>
        <p style={s.subtitle}>Assign and manage mentor relationships between tutors.</p>
        <p style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "0.35rem", margin: "0.35rem 0 0 0" }}>
          Tutors are synced from TC only once daily around 1:00 AM America/Los_Angeles time.
        </p>
      </div>

      <div style={s.tabs}>
        <button
          style={{ ...s.tab, ...(activeTab === "tutors" ? s.tabActive : {}) }}
          onClick={() => setActiveTab("tutors")}
        >
          By Tutor
        </button>
        <button
          style={{ ...s.tab, ...(activeTab === "mentors" ? s.tabActive : {}) }}
          onClick={() => setActiveTab("mentors")}
        >
          By Mentor
        </button>
      </div>

      {activeTab === "tutors" ? (
        <>
          <div style={s.controls}>
            <input
              style={s.search}
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.875rem", color: "#374151", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={showDormant}
                onChange={(e) => setShowDormant(e.target.checked)}
              />
              Show Dormant Tutors
            </label>
            <span style={s.count}>Showing {filtered.length} of {allTutors.length} tutors</span>
          </div>

          {filtered.length === 0 ? (
            <div style={s.emptyState}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "0.5rem" }}>No tutors found</h3>
              <p style={{ fontSize: "0.875rem" }}>
                {searchTerm ? "No tutors match your search." : "No tutors available yet."}
              </p>
            </div>
          ) : (
            <div style={s.grid}>
              {filtered.map((tutor) => (
                <TutorCard key={tutor.canonicalTutorId} tutor={tutor} onManage={openModal} />
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div style={s.controls}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.875rem", color: "#374151", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={showDormant}
                onChange={(e) => setShowDormant(e.target.checked)}
              />
              Show Dormant Tutors
            </label>
            <span style={s.count}>
              {mentorGroups.filter(g => g.mentorName).length} mentors · {tutors.length} tutors
            </span>
          </div>

          {mentorGroups.map((group) => (
            <div key={group.mentorName || "__unassigned__"} style={s.groupCard}>
              <div style={s.groupHeader}>
                {group.mentorName
                  ? <span style={s.groupName}>{group.mentorName}</span>
                  : <span style={{ ...s.groupName, color: "#9ca3af", fontStyle: "italic" }}>No Mentor Assigned</span>}
                <span style={s.groupCount}>{group.tutors.length}</span>
              </div>
              <div>
                {group.tutors
                  .slice()
                  .sort((a, b) => {
                    const nameA = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
                    const nameB = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                  .map((t) => {
                    const name = `${t.firstName || ""} ${t.lastName || ""}`.trim() || "Unnamed";
                    return (
                      <div key={t.canonicalTutorId} style={s.tutorRow}>
                        <span>{name}</span>
                        {t.email && <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>{t.email}</span>}
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </>
      )}

      {isModalOpen && selectedTutorId && (
        <MentorAssignmentManager
          tutorId={selectedTutorId}
          tutorName={selectedTutorName}
          onClose={closeModal}
        />
      )}


<style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default MentorsPage;
